<template>
  <div v-if="isOffline" class="offline-banner" role="status">
    You're offline — the app shell has loaded from cache, but nothing requiring the network (login, rosters, timesheets) will work until you're back online.
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';

// Phase-A1 (2026-08-04, Task 2.4) — the "minimal honest version" of
// offline handling. What this is: a passive navigator.onLine banner,
// mounted globally in App.vue, layered on top of whatever screen is
// already showing (including the login screen). Deliberately built this
// way, not as a routing/redirect decision, because the brief's own HARD
// CONSTRAINT for this round is zero changes to auth flows, login, session
// restore, or the interceptor. A user who's offline on cold boot will still
// land on /login (ensureSessionRestored()'s network call fails and
// combinedGuard's existing not-authenticated fallback fires, completely
// unchanged) — this banner is what tells them WHY, on top of that same
// screen, instead of leaving "logged out" and "offline" indistinguishable.
//
// What this is NOT: it doesn't change where the router sends anyone, catch
// a network error inside ensureSessionRestored()/combinedGuard, or add a
// dedicated offline route. That deeper version (routing an offline+
// unauthenticated boot to a real "you're offline" screen instead of the
// login screen) is Option B/C from the ticket — it needs to inspect
// session-restore's own failure path to tell "offline" apart from
// "genuinely logged out", which is exactly the surface this round's hard
// constraint puts off-limits. Recorded here, not built.
export default {
  name: 'OfflineBanner',
  setup() {
    const isOffline = ref(!navigator.onLine);

    const setOffline = () => { isOffline.value = true; };
    const setOnline = () => { isOffline.value = false; };

    onMounted(() => {
      window.addEventListener('offline', setOffline);
      window.addEventListener('online', setOnline);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('offline', setOffline);
      window.removeEventListener('online', setOnline);
    });

    return { isOffline };
  },
};
</script>

<style scoped>
.offline-banner {
  background-color: var(--orange);
  color: white;
  padding: calc(10px + var(--safe-top)) 10px 10px;
  text-align: center;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1003;
  font-size: 13px;
}
</style>
